import { useState } from "react";
import {
  Button,
  Divider,
  Form,
  FormInstance,
  Input,
  Modal,
  Tooltip
} from "antd";
import { DownOutlined, EditOutlined, RightOutlined } from "@ant-design/icons";
import { SidepanelStyles } from "constants/index";
import {
  useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import { ContextColumnConfigType } from "../models";

const checkIfChildFieldsAreCustomizable = (col: ContextColumnConfigType) => {
  return col.fields?.every((field) => field.customizable);
};

/** Merge sparse Form values (only edited caption_en fields) back into full column config. */
const mergeCaptionUpdates = (
  original: ContextColumnConfigType[],
  updates: Array<{ caption_en?: string; fields?: any[] } | undefined | null>
): ContextColumnConfigType[] => {
  return original.map((col, index) => {
    const update = updates?.[index];
    const merged: ContextColumnConfigType = {
      ...col,
      ...(update?.caption_en === undefined
        ? {}
        : { caption_en: update.caption_en })
    };

    if (col.fields?.length) {
      merged.fields = mergeCaptionUpdates(col.fields, update?.fields ?? []);
    }

    return merged;
  });
};

function getDuplicateValidator(
  form: FormInstance,
  siblingPath: (string | number)[]
) {
  return async (_: any, value: string) => {
    const trimmed = value?.trim()?.toLowerCase();
    if (!trimmed) return Promise.resolve();

    const siblings: ContextColumnConfigType[] =
      form.getFieldValue(siblingPath) ?? [];
    const count = siblings.filter(
      (c) => c?.caption_en?.trim()?.toLowerCase() === trimmed
    ).length;

    if (count > 1) {
      return Promise.reject(
        new Error("Duplicate column names are not allowed")
      );
    }
    return Promise.resolve();
  };
}

function RenameColumnEditor({
  field,
  originalCol,
  form,
  siblingPath,
  level = 0
}: {
  field: { key: number; name: number };
  originalCol: ContextColumnConfigType;
  form: FormInstance;
  siblingPath: (string | number)[];
  level: number;
}) {
  const padding = (level + 1) * 32;
  const isRenamed = !(
    originalCol.caption_en === null ||
    originalCol.caption_en === "" ||
    originalCol.caption_en === originalCol.original_caption_en
  );
  const [isEditMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(true);

  if (!originalCol.customizable) {
    return null;
  }

  const childSiblingPath = [...siblingPath, field.name, "fields"];

  return (
    <div
      className="flex flex-col w-full"
      data-testid={`rename-column-row-${originalCol.field_key}`}
    >
      <div className="flex w-full items-center justify-between text-[13px] text-ciq-text-900">
        <div className="flex space-x-1">
          {originalCol.fields?.length &&
            checkIfChildFieldsAreCustomizable(originalCol) &&
            (expanded ? (
              <DownOutlined
                onClick={() => setExpanded(false)}
                data-testid={`rename-column-collapse-${originalCol.field_key}`}
              />
            ) : (
              <RightOutlined
                onClick={() => setExpanded(true)}
                data-testid={`rename-column-expand-${originalCol.field_key}`}
              />
            ))}
          <div
            className={
              originalCol.allow_rename
                ? ""
                : "italic text-black text-opacity-50"
            }
          >
            {" "}
            {originalCol.caption_en}
          </div>
        </div>

        {originalCol.allow_rename && !isEditMode && (
          <Tooltip
            title="Rename"
            placement="left"
            className="flex space-x-2"
            data-testid={`rename-column-tooltip-${originalCol.field_key}`}
          >
            {isRenamed && (
              <span className="bg-ciq-golden-purple-100 border-ciq-golden-purple-400 text-ciq-golden-purple-950 border border-solid px-2 py-px rounded">
                Edited
              </span>
            )}

            <EditOutlined
              onClick={() => setEditMode(true)}
              className="cursor-pointer text-gray-500 hover:text-black"
              data-testid={`rename-column-edit-${originalCol.field_key}`}
            />
          </Tooltip>
        )}
      </div>

      {isRenamed && !isEditMode && (
        <div className="text-xs italic text-black text-opacity-50">
          (Original: {originalCol.original_caption_en})
        </div>
      )}

      {isEditMode && (
        <Form.Item
          name={[field.name, "caption_en"]}
          className="mb-0 pt-2"
          validateTrigger={["onChange"]}
          rules={[{ validator: getDuplicateValidator(form, siblingPath) }]}
          data-testid={`rename-column-form-item-${originalCol.field_key}`}
        >
          <Input
            className="text-xs"
            placeholder="Enter New Name for Column"
            allowClear
            data-testid={`rename-column-input-${originalCol.field_key}`}
          />
        </Form.Item>
      )}

      {expanded &&
      originalCol.fields?.length &&
      checkIfChildFieldsAreCustomizable(originalCol) ? (
        <div
          style={{ paddingLeft: padding }}
          className="flex flex-col space-y-5 pt-8"
          data-testid={`rename-column-children-${originalCol.field_key}`}
        >
          <Form.List name={[field.name, "fields"]}>
            {(childFields) =>
              childFields.map((childField, idx) => (
                <RenameColumnEditor
                  key={originalCol.fields![idx].field_key}
                  field={childField}
                  originalCol={originalCol.fields![idx]}
                  form={form}
                  siblingPath={childSiblingPath}
                  level={level + 1}
                />
              ))
            }
          </Form.List>
        </div>
      ) : null}
    </div>
  );
}

function RenameColumnsModal({
  isOpen,
  setIsOpen,
  onUpdateColumnConfig
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  onUpdateColumnConfig: (config: ContextColumnConfigType[]) => void;
}) {
  const dispatch = useCustomReportsDispatch() as any;
  const { columnConfig } = useCustomReports();
  const [form] = Form.useForm();
  const [hasErrors, setHasErrors] = useState(false);

  const setContextColumnConfig = (config: ContextColumnConfigType[]) => {
    dispatch({ type: "UPDATE_COLUMN_CONFIG", payload: config });
    onUpdateColumnConfig(config);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const merged = mergeCaptionUpdates(
        columnConfig ?? [],
        values.columns ?? []
      );
      setContextColumnConfig(merged);
      setIsOpen(false);
    } catch {
      // validation failed — errors are shown inline by Form.Item
    }
  };

  return (
    <Modal
      className="custom-drawer"
      title="Rename Column Headers"
      width={420}
      style={SidepanelStyles.style}
      mask={{ closable: true }}
      footer={
        <div className="flex space-x-2" data-testid="rename-columns-footer">
          <Button
            onClick={() => setIsOpen(false)}
            data-testid="rename-columns-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="grow px-20"
            disabled={hasErrors}
            onClick={handleSave}
            data-testid="rename-columns-save"
          >
            Save
          </Button>
        </div>
      }
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      destroyOnHidden
      data-testid="rename-columns-modal"
    >
      <Form
        form={form}
        initialValues={{
          columns: columnConfig ? structuredClone(columnConfig) : []
        }}
        onFieldsChange={(_, allFields) => {
          setHasErrors(allFields.some((f) => (f.errors?.length ?? 0) > 0));
        }}
      >
        <div className="space-y-4 w-full text-xs">
          <div
            className="flex text-xs space-x-2 items-center"
            data-testid="rename-columns-info-banner"
          >
            <div className="border border-solid border-ciq-blue-300 rounded-lg p-1 font-bold text-xs bg-ciq-blue-100">
              .CSV
            </div>
            <div className="text-black text-opacity-80 text-xs">
              The renamed columns will be visible within this report and its
              export.
            </div>
          </div>

          <Divider />
          <div className="text-ciq-gray-900 text-opacity-80 font-medium text-xs">
            Columns
          </div>

          <div
            className="flex flex-col space-y-8 pl-2"
            data-testid="rename-columns-list"
          >
            <Form.List name="columns">
              {(fields) =>
                fields.map((field, index) => (
                  <RenameColumnEditor
                    key={columnConfig![index].field_key}
                    field={field}
                    originalCol={columnConfig![index]}
                    form={form}
                    siblingPath={["columns"]}
                    level={0}
                  />
                ))
              }
            </Form.List>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export default RenameColumnsModal;
